import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { endOfDay, isValid } from 'date-fns';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useCreateUserGiftMutation, useFetchGiftTypesQuery } from '../../redux/features/userGift/userGift.apiSlice';
import { GiftTargetType, GiftTypes } from '../../types/userGift';
import {
  describeGiftError,
  GIFT_TYPE_DESCRIPTIONS,
  GIFT_TYPE_LABELS,
  PREMIUM_MONTHS_MAX,
  PREMIUM_MONTHS_MIN,
  TARGET_TYPE_LABELS,
  TARGET_VALUE_HINTS,
  validateGiftTarget,
} from './userGiftCopy';

type FormValues = {
  giftTypeId: number | '';
  targetType: GiftTargetType;
  targetValue: string;
  expiresAt: Date | null;
  premiumMonths: string;
  note: string;
};

const initialValues: FormValues = {
  giftTypeId: '',
  targetType: GiftTargetType.USER_ID,
  targetValue: '',
  expiresAt: null,
  premiumMonths: '',
  note: '',
};

const validate = (values: FormValues, isPremium: boolean) => {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  const targetError = validateGiftTarget(values.targetType, values.targetValue);

  if (!values.giftTypeId) {
    errors.giftTypeId = 'Required';
  }

  if (targetError) {
    errors.targetValue = targetError;
  }

  if (!values.expiresAt) {
    errors.expiresAt = 'Required';
  } else if (!isValid(values.expiresAt)) {
    // A half-typed date is an Invalid Date: its NaN time passes the "future" check, then toISOString() throws.
    errors.expiresAt = 'Enter a valid date';
  } else if (endOfDay(values.expiresAt).getTime() <= Date.now()) {
    errors.expiresAt = 'Must be in the future';
  }

  if (isPremium) {
    const months = Number(values.premiumMonths);

    if (!values.premiumMonths) {
      errors.premiumMonths = 'Required';
    } else if (!Number.isInteger(months) || months < PREMIUM_MONTHS_MIN || months > PREMIUM_MONTHS_MAX) {
      errors.premiumMonths = `Whole months, ${PREMIUM_MONTHS_MIN} to ${PREMIUM_MONTHS_MAX}`;
    }
  }

  return errors;
};

interface AddUserGiftDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddUserGiftDialog({ open, onClose }: AddUserGiftDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: giftTypes } = useFetchGiftTypesQuery(undefined, { skip: !open });
  const [createGift, { isLoading }] = useCreateUserGiftMutation();
  const [saveError, setSaveError] = useState('');
  const typeOf = (giftTypeId: number | '') => giftTypes?.find(({ id }) => id === giftTypeId)?.type;
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm, resetForm } =
    useFormik<FormValues>({
      initialValues,
      validate: (formValues) => validate(formValues, typeOf(formValues.giftTypeId) === GiftTypes.PREMIUM),
      onSubmit: async (formValues) => {
        setSaveError('');

        try {
          await createGift({
            giftTypeId: Number(formValues.giftTypeId),
            targetType: formValues.targetType,
            targetValue: formValues.targetValue.trim(),
            // The gift works through the whole chosen day.
            expiresAt: endOfDay(formValues.expiresAt as Date).toISOString(),
            ...(typeOf(formValues.giftTypeId) === GiftTypes.PREMIUM
              ? { premiumMonths: Number(formValues.premiumMonths) }
              : {}),
            note: formValues.note.trim() || null,
          }).unwrap();
          enqueueSnackbar('Gift created.', { variant: 'success' });
          resetForm();
          onClose();
        } catch (err) {
          setSaveError(describeGiftError(err));
        }
      },
    });

  const handleClose = () => {
    setSaveError('');
    resetForm();
    onClose();
  };

  const selectedType = typeOf(values.giftTypeId);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add gift</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>
          {GIFT_TYPE_DESCRIPTIONS[selectedType ?? GiftTypes.FREE_MANAGEMENT_FEE]}
        </Alert>
        <TextField
          select
          margin="normal"
          fullWidth
          id="user-gift-giftTypeId"
          name="giftTypeId"
          label="Gift type"
          value={values.giftTypeId}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.giftTypeId && Boolean(errors.giftTypeId)}
          helperText={touched.giftTypeId && errors.giftTypeId}
        >
          {(giftTypes || [])
            .filter(({ isActive }) => isActive)
            .map(({ id, type }) => (
              <MenuItem key={id} value={id}>
                {GIFT_TYPE_LABELS[type] || type}
              </MenuItem>
            ))}
        </TextField>
        <FormControl margin="normal" fullWidth>
          <FormLabel id="user-gift-targetType-label">Target</FormLabel>
          <RadioGroup
            row
            aria-labelledby="user-gift-targetType-label"
            name="targetType"
            value={values.targetType}
            onChange={(event) => {
              setFieldValue('targetType', event.target.value);
              setFieldValue('targetValue', '');
            }}
          >
            {Object.values(GiftTargetType).map((targetType) => (
              <FormControlLabel
                key={targetType}
                value={targetType}
                control={<Radio />}
                label={TARGET_TYPE_LABELS[targetType]}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <TextField
          margin="normal"
          fullWidth
          id="user-gift-targetValue"
          name="targetValue"
          label={TARGET_TYPE_LABELS[values.targetType]}
          value={values.targetValue}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.targetValue && Boolean(errors.targetValue)}
          helperText={(touched.targetValue && errors.targetValue) || TARGET_VALUE_HINTS[values.targetType]}
          inputProps={{ dir: 'ltr', maxLength: 255 }}
        />
        <DatePicker
          label="Expires at"
          value={values.expiresAt}
          onChange={(date) => setFieldValue('expiresAt', date)}
          disablePast
          slotProps={{
            textField: {
              id: 'user-gift-expiresAt',
              name: 'expiresAt',
              fullWidth: true,
              margin: 'normal',
              error: touched.expiresAt && Boolean(errors.expiresAt),
              helperText:
                (touched.expiresAt && errors.expiresAt) ||
                (selectedType === GiftTypes.PREMIUM
                  ? 'Last day the gift can be applied'
                  : 'The gift works until the end of this day'),
            },
          }}
        />
        {selectedType === GiftTypes.PREMIUM && (
          <TextField
            margin="normal"
            fullWidth
            type="number"
            id="user-gift-premiumMonths"
            name="premiumMonths"
            label="Premium months"
            value={values.premiumMonths}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.premiumMonths && Boolean(errors.premiumMonths)}
            helperText={(touched.premiumMonths && errors.premiumMonths) || 'Counted from the day the gift is applied'}
            inputProps={{ min: PREMIUM_MONTHS_MIN, max: PREMIUM_MONTHS_MAX, step: 1 }}
          />
        )}
        <TextField
          margin="normal"
          fullWidth
          id="user-gift-note"
          name="note"
          label="Note (optional)"
          value={values.note}
          onChange={handleChange}
          inputProps={{ maxLength: 500 }}
          helperText="Campaign name or reason"
        />
        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isLoading}
          loadingPosition="end"
          onClick={submitForm}
        >
          Save
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
